import { useState } from 'react';
import { Link } from 'react-router-dom';
import { signIn } from '../lib/auth';

// Gate for the whole site. Shown by <App> whenever there is no session.
//
// Credentials are the player's IN-GAME username and password — there is no
// separate site account. Anyone without one goes to /join, which stays
// reachable without signing in because redeeming a signup code is how an
// account gets created in the first place.

export function SignIn() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(username.trim(), password);
      // No navigation needed — <App> re-renders on the auth change and
      // drops the player on whatever route they originally asked for.
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-sm py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
      <p className="text-sm text-ink-400 mt-1">
        Use the same username and password you use in game.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label className="block text-xs text-ink-400 mb-1.5" htmlFor="u">Username</label>
          <input
            id="u"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
            className="w-full rounded-md bg-ink-900 border border-ink-800 px-3 py-2 text-sm outline-none focus:border-ink-600"
          />
        </div>

        <div>
          <label className="block text-xs text-ink-400 mb-1.5" htmlFor="p">Password</label>
          <input
            id="p"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="w-full rounded-md bg-ink-900 border border-ink-800 px-3 py-2 text-sm outline-none focus:border-ink-600"
          />
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <button
          type="submit"
          disabled={busy || !username.trim() || !password}
          className="w-full rounded-md bg-ink-100 text-ink-900 text-sm font-medium py-2 disabled:opacity-50"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="text-xs text-ink-400 mt-6">
        Don't have an account yet? <Link className="underline hover:text-ink-200" to="/join">Redeem a signup code</Link>.
      </p>
    </div>
  );
}
